import { useEffect, useState } from 'react';
import { execApi } from '../../../services/api';
import { BonusFields, BonusDraft, BonusValues, toDraft, toPayload } from './BonusFields';

// The single place to edit a therapist's standing comp config (Ohad 2026-07-23:
// all editing lives in the accounting hub; the salary dashboard just computes).
// ₪ PER MEETING is the model — the engine prices from these directly. Employment
// (מורשה/פטור/שכיר) + withholding feed the net-pay computation.

export interface Rates extends BonusValues {
  workedRate: number; nonClinicalRate: number;
  oncallRate: number;               // ₪ per on-call day, priced from Basel's schedule
  tariffIntakeAdult: number; tariffIntakeYouth: number;
  tariffFollowupAdult: number; tariffFollowupYouth: number;
  // Slot length per meeting kind. Not what the meeting PAYS (the tariff is), but what
  // length is expected — the audit flags a calendar slot that disagrees with it.
  intakeLengthAdults: number; intakeLengthYouth: number;
  followupLengthAdults: number; followupLengthYouth: number;
  noshowPct: number;               // share of a meeting's tariff paid on a no-show
  overridden?: string[];           // fields set for THIS therapist
  from_global?: string[];          // fields inherited from the roster-wide policy
}
interface Billing {
  employment: string; vat_registered: boolean; withholding_rate: number;
  invoice_type?: string;           // tax = חשבונית מס | deal = חשבונית עסקה
  email?: string | null;           // where the report link is sent, when set here
  email_default?: string | null;   // the clinical system's address — used when `email` is empty
  phone?: string | null;           // from the clinical record; nothing dials it yet
}
interface Props {
  therapist: {
    name: string; hebrew_name: string | null; role?: string;
    rates: Rates; billing: Billing; archived?: boolean;
    // On Basel's on-call schedule (/exec/oncall). Only then is there an on-call
    // tariff to edit — everyone else saw "1500 לכוננות" and read it as pay.
    oncall_doctor?: boolean;
    // This month's computed pay — used to warn before a deletion drops a real payment.
    total_payment?: number; completed_meetings?: number; noshow_meetings?: number;
  };
  month: string;
  isNew?: boolean;                 // adding a brand-new employee
  onClose: () => void;
  onSaved: () => void;
  // What the calendar says this person is, when the editor was opened from the
  // unmatched banner. A suggestion only — Basel can still change it, but the default
  // stops being "Case Manager" for a doctor who only ever holds psychiatrist
  // meetings (Ohad, 2026-10-06).
  suggestedType?: string;
}

// Employment × VAT collapse to one 3-way choice, the way accounting thinks of it.
type EmpKind = 'licensed' | 'exempt' | 'salaried';
const empOf = (b: Billing): EmpKind =>
  b.employment === 'salaried' ? 'salaried' : b.vat_registered ? 'licensed' : 'exempt';
const empToProfile = (e: EmpKind) =>
  e === 'salaried' ? { employment: 'salaried', vat_registered: false }
    : e === 'exempt' ? { employment: 'freelancer', vat_registered: false }
      : { employment: 'freelancer', vat_registered: true };

type TariffField = 'tariffIntakeAdult' | 'tariffIntakeYouth'
  | 'tariffFollowupAdult' | 'tariffFollowupYouth';
type LengthField = 'intakeLengthAdults' | 'intakeLengthYouth'
  | 'followupLengthAdults' | 'followupLengthYouth';

// Each meeting kind carries two numbers, and they answer different questions: the
// tariff is what it PAYS, the length is how long the slot is EXPECTED to be. The
// audit compares that length to the calendar and flags the gap, so a tariff fixed
// without its length leaves the meeting paid correctly but still marked irregular
// (Dr. Zahalka's youth follow-up, Ohad 2026-08-12) — hence they are edited together.
const TARIFFS: { field: TariffField; len: LengthField; label: string }[] = [
  { field: 'tariffIntakeAdult', len: 'intakeLengthAdults', label: 'אינטייק · מבוגר' },
  { field: 'tariffIntakeYouth', len: 'intakeLengthYouth', label: 'אינטייק · נוער' },
  { field: 'tariffFollowupAdult', len: 'followupLengthAdults', label: 'מעקב · מבוגר' },
  { field: 'tariffFollowupYouth', len: 'followupLengthYouth', label: 'מעקב · נוער' },
];

// ₪ per meeting and ₪ per hour are two ways of saying the same thing, and the engine
// prefers the per-meeting tariff (exec_salary._done_amount) — the hourly is only the
// fallback when no tariff is set. Basel thinks in whichever he was given, so the editor
// converts both ways instead of making him do the arithmetic (Ohad 2026-08-31).
const perMeeting = (hourly: number, minutes: number) =>
  hourly > 0 && minutes > 0 ? Math.round((minutes / 60) * hourly) : 0;
const perHour = (tariff: number, minutes: number) =>
  tariff > 0 && minutes > 0 ? Math.round(tariff / (minutes / 60)) : 0;

// Default meeting lengths for a NEW therapist, measured from the roster on 2026-10-06.
// A hand-added therapist used to be born with every length 0, which made the "fill from
// hourly" button derive ₪0 four times and priced their meetings at ₪0.00 — Vered Levy
// and Rami Khouri both sat like that. Case-manager lengths are unanimous (35 of 35);
// the psychiatrist youth figures are the mode of a near-tie (45 over 30, 30 over 15),
// so they are a starting point to correct, not a fact.
const DEFAULT_LENGTHS: Record<string, Record<LengthField, number>> = {
  'Case Manager': {
    intakeLengthAdults: 30, intakeLengthYouth: 30,
    followupLengthAdults: 10, followupLengthYouth: 10,
  },
  Psychiatrist: {
    intakeLengthAdults: 30, intakeLengthYouth: 45,
    followupLengthAdults: 15, followupLengthYouth: 30,
  },
};

// One of the people Rana opened in the clinical system, as the roster sees them.
interface ClinicalUser {
  system_user_id: number;
  name: string;
  hebrew_name: string;
  role: string;
  therapist_type: string;
  email: string;
  phone: string;
  active: boolean;
  on_roster: string | null;   // the roster name, when they are already on it
}

export function TherapistCompEditor({ therapist: t, month, isNew, suggestedType, onClose, onSaved }: Props) {
  // New-employee identity fields (name must match meetings_raw for meetings to attach).
  const [name, setName] = useState(t.name);
  const [hebrew, setHebrew] = useState(t.hebrew_name || '');
  // Seeded from the row for an existing person, so opening the editor and saving
  // cannot silently demote a psychiatrist to a case manager.
  const [ttype, setTtype] = useState(
    isNew ? (suggestedType || 'Case Manager')
      : t.role === 'psy' ? 'Psychiatrist' : 'Case Manager');
  const [ttypeTouched, setTtypeTouched] = useState(false);
  const [sync, setSync] = useState<string | null>(null);

  // A new employee is PICKED from the clinical system, not typed. Typing resolved the
  // name backwards, and a one-letter spelling difference opened a second person with
  // no clinical id, no meetings and no pay — ד"ר טארק זרעיני against the system's
  // טארק זיריני, 2026-10-25. The id travels with the pick, so that cannot recur.
  const [clinical, setClinical] = useState<ClinicalUser[]>([]);
  const [picked, setPicked] = useState<number | null>(null);
  // The escape hatch, kept because it is a real case: someone paid who has no clinical
  // record at all. Explicit, never the default — that is the whole point of the change.
  const [manual, setManual] = useState(false);
  const [loadErr, setLoadErr] = useState(false);

  useEffect(() => {
    if (!isNew) return;
    execApi.clinicalUsers()
      .then((r) => setClinical((r.data as { users: ClinicalUser[] }).users || []))
      .catch(() => setLoadErr(true));
  }, [isNew]);

  // Picking fills everything the clinical record already knows. The role follows the
  // pick unless it was changed by hand, and the email is only a default: it is sent
  // only when touched, exactly as for an existing therapist.
  const pick = (id: number | null) => {
    setPicked(id);
    const u = clinical.find((c) => c.system_user_id === id);
    if (!u) return;
    setName(u.name);
    setHebrew(u.hebrew_name || '');
    if (!ttypeTouched) setTtype(u.therapist_type);
    if (!emailTouched) setEmail(u.email || '');
    if (!phoneTouched) setPhone(u.phone || '');
  };
  const pickedUser = clinical.find((c) => c.system_user_id === picked);
  const [emp, setEmp] = useState<EmpKind>(empOf(t.billing));
  const [invType, setInvType] = useState(t.billing.invoice_type || 'tax');
  const [wh, setWh] = useState(String(Math.round((t.billing.withholding_rate || 0) * 100)));
  // Blank means "leave whatever is stored" — the field is only sent when touched, so
  // opening the editor to change a tariff can never wipe an address it never showed.
  const [email, setEmail] = useState(t.billing.email || '');
  const [emailTouched, setEmailTouched] = useState(false);
  const [phone, setPhone] = useState(t.billing.phone || '');
  const [phoneTouched, setPhoneTouched] = useState(false);
  const [noshowPct, setNoshowPct] = useState(String(t.rates.noshowPct ?? 0));
  const [worked, setWorked] = useState(String(t.rates.workedRate));
  const [nonClinical, setNonClinical] = useState(String(t.rates.nonClinicalRate));
  const [oncall, setOncall] = useState(String(t.rates.oncallRate ?? 1500));
  // NOT rounded. Half-shekel tariffs are real — a 15-minute follow-up at a ₪450 hourly
  // is ₪112.50 — and rounding on OPEN meant merely looking at such a therapist and
  // pressing save silently repriced them to ₪113 (Ohad 2026-08-31).
  const [tar, setTar] = useState<Record<string, string>>(() =>
    Object.fromEntries(TARIFFS.map((m) => [m.field, String(t.rates[m.field] ?? 0)])));
  const [len, setLen] = useState<Record<string, string>>(() =>
    Object.fromEntries(TARIFFS.map((m) => [m.len,
      String(t.rates[m.len] || (isNew ? DEFAULT_LENGTHS[ttype][m.len] : 0))])));
  const [lenTouched, setLenTouched] = useState(false);
  // The role picker is right above these fields, and flipping it changes what a
  // sensible length is. Until the lengths are edited by hand they follow the role.
  useEffect(() => {
    if (!isNew || lenTouched) return;
    setLen(Object.fromEntries(TARIFFS.map((m) => [m.len, String(DEFAULT_LENGTHS[ttype][m.len])])));
  }, [ttype, isNew, lenTouched]);
  // Which tariff rows can actually be derived. A row with no length cannot: the
  // formula is hourly x length, so it would write 0 and read as "the button is broken".
  const withLength = TARIFFS.filter((m) => Number(len[m.len]) > 0);
  const hasAnyLength = withLength.length > 0;
  // Meetings already priced at 0 because nothing is configured — the same silence that
  // hid Rami Khouri's 47 meetings and Vered Levy's whole card (Ohad, 2026-10-06).
  const noComp = Number(worked) <= 0 && TARIFFS.every((m) => !(Number(tar[m.field]) > 0));

  const [saving, setSaving] = useState(false);
  // What the clinical system calls this person in Hebrew — offered as one click so a
  // name is never re-transliterated by hand (Ohad 2026-07-27). Suggestion only.
  const [suggestion, setSuggestion] = useState('');
  useEffect(() => {
    if (isNew) return;
    execApi.getHebNameSuggestion(t.name)
      .then((r) => setSuggestion((r.data as { hebrew_name: string | null }).hebrew_name || ''))
      .catch(() => {});
  }, [t.name, isNew]);

  // Bonus: shown for psychiatrists (case managers have none). Editing here writes a
  // PERSONAL policy for this doctor, which outranks the roster-wide default.
  const isPsy = isNew ? ttype === 'Psychiatrist' : t.role !== 'cm';
  const [bonus, setBonus] = useState<BonusDraft>(() => toDraft(t.rates));
  const [bonusEnabled, setBonusEnabled] = useState(!!t.rates.bonusEnabled);
  const [bonusTouched, setBonusTouched] = useState(false);
  const personalBonus = (t.rates.overridden || []).some((f) => f.startsWith('bonus'));

  // Three writes in a row, and the first one failing used to kill the two after it in
  // total silence: no catch here, no axios error interceptor, so the dialog just sat
  // there. A closed month answers 409 on /salary/rates and 400 on /salary/profile, and
  // Basel read that as "the email is not saved" and "the tariff does not update" — two
  // separate bug reports, one swallowed error (Ohad, 2026-10-06).
  const [error, setError] = useState<string | null>(null);

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      let key = isNew ? name.trim() : t.name;
      if (isNew) {
        if (manual ? !key : picked === null) { setSaving(false); return; }
        const res = await execApi.addTherapist(manual
          ? { name: key, hebrew_name: hebrew.trim(), therapist_type: ttype }
          : { system_user_id: picked as number, therapist_type: ttype });
        const d = res.data as { matched: boolean; meetings_this_month: number; name: string };
        // The roster keeps whatever spelling it already holds for this clinical id, so
        // the name that comes back is not always the one that went in. The two writes
        // below join on it — using the typed name here wrote their tariffs and profile
        // onto a key nobody reads.
        if (d.name) key = d.name;
        setSync(d.meetings_this_month > 0
          ? `נמצאו ${d.meetings_this_month} פגישות לשם הזה החודש ✓`
          : 'לא נמצאו פגישות לשם הזה — ודא שהשם זהה ליומן');
      }
      await execApi.saveSalaryRates({
        therapist: key, month,
        rates: {
          workedRate: Number(worked) || 0,
          nonClinicalRate: Number(nonClinical) || 0,
          oncallRate: Number(oncall) || 0,
          tariffIntakeAdult: Number(tar.tariffIntakeAdult) || 0,
          tariffIntakeYouth: Number(tar.tariffIntakeYouth) || 0,
          tariffFollowupAdult: Number(tar.tariffFollowupAdult) || 0,
          tariffFollowupYouth: Number(tar.tariffFollowupYouth) || 0,
          intakeLengthAdults: Number(len.intakeLengthAdults) || 0,
          intakeLengthYouth: Number(len.intakeLengthYouth) || 0,
          followupLengthAdults: Number(len.followupLengthAdults) || 0,
          followupLengthYouth: Number(len.followupLengthYouth) || 0,
          noshowPct: Number(noshowPct) || 0,
          // Only written when actually edited here — otherwise saving any other field
          // would silently pin a personal copy and cut this doctor off from the
          // roster-wide policy for good.
          ...(bonusTouched ? toPayload(bonus, bonusEnabled) : {}),
        },
      });
      const prof = empToProfile(emp);
      await execApi.saveSalaryProfile({
        therapist: key, ...prof, withholding_rate: (Number(wh) || 0) / 100,
        invoice_type: invType, month,
        ...(emailTouched ? { email: email.trim() } : {}),
        ...(phoneTouched ? { phone: phone.trim() } : {}),
        ...(!isNew && ttypeTouched ? { therapist_type: ttype } : {}),
      });
      // An existing therapist's English name is the calendar key and never moves;
      // only the Hebrew display name is writable, and only when actually changed.
      if (!isNew && hebrew.trim() && hebrew.trim() !== (t.hebrew_name || '')) {
        await execApi.setDisplayName({ therapist: t.name, hebrew_name: hebrew.trim() });
      }
      onSaved();
      if (!isNew) onClose();   // for a new employee, stay open to show the sync check
    } catch (e) {
      // Say WHICH call failed and how. "השמירה נכשלה" on its own sent us round the
      // houses on 2026-10-06: every write in the server log was 200, so the thing
      // that threw never reached the server at all — and the message could not tell
      // us that. A request that got no response has no status; saying so is the clue.
      const err = e as {
        response?: { data?: { detail?: string }; status?: number };
        config?: { url?: string; method?: string }; message?: string; code?: string;
      };
      const where = (err.config?.url || '').split('/api/exec').pop() || '';
      const detail = err.response?.data?.detail;
      setError(detail
        ? `${detail}${where ? ` · ${where}` : ''}`
        : err.response
          ? `השמירה נכשלה (${err.response.status})${where ? ` · ${where}` : ''}`
          : `לא התקבלה תשובה מהשרת${where ? ` · ${where}` : ''}` +
            `${err.code ? ` · ${err.code}` : ''} — ייתכן שחלק מהנתונים כן נשמרו`);
    } finally { setSaving(false); }
  };

  const archive = async () => {
    await execApi.setRosterState({
      therapist: t.name, state: t.archived ? 'active' : 'archived', month,
    });
    onSaved();
    onClose();
  };

  // Delete = gone from this month onwards. Earlier months keep the therapist and keep
  // paying them, which is why the confirmation says so out loud — and if the month
  // being deleted from carries unpaid work, the confirmation says that first (Ohad
  // 2026-07-27): deleting someone mid-month must never quietly drop a real payment.
  const remove = async () => {
    const label = t.hebrew_name || t.name;
    const pay = t.total_payment || 0;
    const meetings = (t.completed_meetings || 0) + (t.noshow_meetings || 0);
    const warning = pay > 0
      ? `⚠️ יש לו תשלום בחודש ${month}: ₪${pay.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
      + (meetings ? ` על ${meetings} פגישות` : '') + '.\n'
      + 'המחיקה תוציא אותו מהחודש הזה — התשלום הזה לא ישולם.\n'
      + 'אם הוא סיים לעבוד בסוף החודש הקודם, מחק אותו מהחודש הבא במקום.\n\n'
      : '';
    if (!window.confirm(
      warning +
      `למחוק את ${label} מחודש ${month} והלאה?\n\n` +
      `חודשים קודמים לא ישתנו — הוא ימשיך להופיע בהם עם התשלום שלו.`)) return;
    await execApi.setRosterState({ therapist: t.name, state: 'deleted', month });
    onSaved();
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center" dir="rtl">
      <div className="absolute inset-0 bg-black/30" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg p-6 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-800">
            {isNew ? 'הוספת עובד' : `${t.hebrew_name || t.name} · תעריפים ופרופיל`}
          </h2>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700 text-xl">✕</button>
        </div>

        <div className="space-y-5">
          {isNew && (
            <div className="flex flex-wrap gap-3 pb-1">
              {!manual ? (
                <label className="text-xs text-gray-500">
                  עובד מהמערכת הקלינית *
                  <select value={picked ?? ''} dir="rtl"
                    onChange={(e) => pick(e.target.value ? Number(e.target.value) : null)}
                    className="block mt-1 w-72 text-sm border border-gray-300 rounded px-2 py-1.5 bg-white">
                    <option value="">— בחר —</option>
                    {clinical.map((u) => (
                      <option key={u.system_user_id} value={u.system_user_id} disabled={!!u.on_roster}>
                        {(u.hebrew_name || u.name)}
                        {u.hebrew_name ? ` · ${u.name}` : ''}
                        {u.active ? '' : ' · לא פעיל'}
                        {u.on_roster ? ' · כבר ברוסטר' : ''}
                      </option>
                    ))}
                  </select>
                </label>
              ) : (
                <>
                  <label className="text-xs text-gray-500">
                    שם (כמו ביומן) *
                    <input type="text" value={name} onChange={(e) => setName(e.target.value)}
                      placeholder="English name" className="block mt-1 w-44 text-sm border border-gray-300 rounded px-2 py-1.5" />
                  </label>
                  <label className="text-xs text-gray-500">
                    שם בעברית
                    <input type="text" value={hebrew} onChange={(e) => setHebrew(e.target.value)}
                      className="block mt-1 w-36 text-sm border border-gray-300 rounded px-2 py-1.5" />
                  </label>
                </>
              )}
              <label className="text-xs text-gray-500">
                מייל לשליחת הדוח
                <input type="email" dir="ltr" value={email}
                  onChange={(e) => { setEmail(e.target.value); setEmailTouched(true); }}
                  placeholder="לפי המערכת הקלינית"
                  className="block mt-1 w-52 text-sm border border-gray-300 rounded px-2 py-1.5" />
              </label>
              <label className="text-xs text-gray-500">
                טלפון
                <input type="tel" dir="ltr" value={phone}
                  onChange={(e) => { setPhone(e.target.value); setPhoneTouched(true); }}
                  placeholder="לפי המערכת הקלינית"
                  className="block mt-1 w-36 text-sm border border-gray-300 rounded px-2 py-1.5" />
              </label>
              <label className="text-xs text-gray-500">
                תפקיד
                <select value={ttype} onChange={(e) => setTtype(e.target.value)}
                  className="block mt-1 w-32 text-sm border border-gray-300 rounded px-2 py-1.5 bg-white">
                  <option value="Case Manager">מנהל תיק</option>
                  <option value="Psychiatrist">פסיכיאטר</option>
                </select>
              </label>
              <div className="w-full text-xs">
                {!manual && pickedUser && (
                  <p className="text-gray-500">
                    נבחר: <span className="text-gray-700">{pickedUser.name}</span>
                    {' · מזהה במערכת '}<span className="text-gray-700">{pickedUser.system_user_id}</span>
                    {' — זה המפתח שעליו מתחברות הפגישות והתעריפים.'}
                  </p>
                )}
                {!manual && (
                  <button type="button" onClick={() => { setManual(true); setPicked(null); setName(''); }}
                    className="mt-1 text-blue-600 hover:underline">
                    לא מופיע ברשימה — להזין ידנית
                  </button>
                )}
                {manual && (
                  <>
                    <p className="text-amber-700">
                      הזנה ידנית: לעובד שאינו קיים במערכת הקלינית בכלל. השם חייב להיות
                      זהה לשם ביומן, אחרת לא יתחברו אליו פגישות.
                    </p>
                    <button type="button" onClick={() => { setManual(false); setName(t.name); }}
                      className="mt-1 text-blue-600 hover:underline">
                      חזרה לבחירה מהרשימה
                    </button>
                  </>
                )}
                {loadErr && !manual && (
                  <p className="text-red-600">לא הצלחתי לטעון את רשימת העובדים — נסה לרענן.</p>
                )}
              </div>
            </div>
          )}
          {!isNew && (
            <div className="pb-1">
              <div className="flex flex-wrap items-end gap-3">
                <label className="text-xs text-gray-500">
                  שם בעברית
                  <input type="text" value={hebrew} onChange={(e) => setHebrew(e.target.value)}
                    placeholder="—"
                    className="block mt-1 w-44 text-sm border border-gray-300 rounded px-2 py-1.5" />
                </label>
                {/* The address the report link is sent to. Blank shows the clinical
                    system's actual address as the placeholder — that is where the
                    report goes (Basel asked to see it, 01/11) — and typing here
                    overrides it without touching the operational system. */}
                <label className="text-xs text-gray-500">
                  מייל לשליחת הדוח
                  <input type="email" dir="ltr" value={email}
                    onChange={(e) => { setEmail(e.target.value); setEmailTouched(true); }}
                    placeholder={t.billing.email ? '' : (t.billing.email_default || 'אין מייל במערכת הקלינית')}
                    title={!t.billing.email && t.billing.email_default
                      ? 'הכתובת מהמערכת הקלינית — הדוח נשלח אליה. הקלדה כאן מחליפה אותה' : undefined}
                    className="block mt-1 w-56 text-sm border border-gray-300 rounded px-2 py-1.5" />
                </label>
                <label className="text-xs text-gray-500">
                  טלפון
                  <input type="tel" dir="ltr" value={phone}
                    onChange={(e) => { setPhone(e.target.value); setPhoneTouched(true); }}
                    placeholder={t.billing.phone ? '' : 'לפי המערכת הקלינית'}
                    className="block mt-1 w-36 text-sm border border-gray-300 rounded px-2 py-1.5" />
                </label>
                {/* Role is editable here because this is where the error surfaces: two
                    doctors were filed as case managers, which silently denied them the
                    psychiatrist bonus (Ohad 2026-08-31). */}
                <label className="text-xs text-gray-500">
                  תפקיד
                  <select value={ttype}
                    onChange={(e) => { setTtype(e.target.value); setTtypeTouched(true); }}
                    className="block mt-1 w-32 text-sm border border-gray-300 rounded px-2 py-1.5 bg-white">
                    <option value="Case Manager">מנהל תיק</option>
                    <option value="Psychiatrist">פסיכיאטר</option>
                  </select>
                </label>
                <div className="text-xs text-gray-500 pb-2">
                  שם ביומן: <span className="text-gray-600">{t.name}</span> · לא ניתן לשינוי (עליו מתחברות הפגישות)
                </div>
              </div>
              {ttypeTouched && (
                <div className="mt-2 text-[11px] text-amber-700 bg-amber-50 rounded px-2 py-1.5">
                  שינוי תפקיד משנה זכאות לבונוס: לפסיכיאטר יש בונוס מגודר, למנהל תיק יש בונוס סטים.
                  התעריפים עצמם אינם משתנים.
                </div>
              )}
              {suggestion && suggestion !== hebrew && (
                <button onClick={() => setHebrew(suggestion)}
                  className="mt-1.5 text-xs text-blue-600 hover:underline">
                  ↓ מהמערכת הקלינית: {suggestion}
                </button>
              )}
            </div>
          )}
          {sync && <div className="text-xs rounded bg-gray-50 text-gray-600 px-3 py-2">{sync}</div>}
          <div className="flex flex-wrap gap-4">
            <div>
              <label className="text-xs font-medium text-gray-500">סוג העסקה</label>
              <select value={emp} onChange={(e) => setEmp(e.target.value as EmpKind)}
                className="block mt-1 w-48 text-sm border border-gray-300 rounded px-2 py-1.5 bg-white">
                <option value="licensed">עוסק מורשה (מע״מ)</option>
                <option value="exempt">עוסק פטור</option>
                <option value="salaried">שכיר</option>
              </select>
            </div>
            <div>
              <label className="text-xs font-medium text-gray-500">סוג חשבונית</label>
              <select value={invType} onChange={(e) => setInvType(e.target.value)}
                className="block mt-1 w-40 text-sm border border-gray-300 rounded px-2 py-1.5 bg-white">
                <option value="tax">חשבונית מס</option>
                <option value="deal">חשבונית עסקה</option>
              </select>
            </div>
          </div>

          <div>
            <div className="flex items-baseline justify-between mb-2">
              <div className="text-xs font-medium text-gray-500">
                תעריף ואורך פגישה <span className="font-normal text-gray-500">· ₪ לפני מע״מ · אורך בדקות</span>
              </div>
              {/* A tariff is hourly x LENGTH, so a zero length derives zero — and the
                  button used to fill four zeros and look broken. Vered Levy was added
                  by hand with every length 0, pressing this wrote ₪0 four times, and
                  her meetings priced at ₪0.00 with no warning (Ohad, 2026-10-06).
                  Now it only fills the rows that have a length, and says which. */}
              <button type="button"
                disabled={!(Number(worked) > 0) || !hasAnyLength}
                title={!(Number(worked) > 0)
                  ? 'צריך תעריף שעתי כדי לגזור'
                  : !hasAnyLength
                    ? 'צריך אורך פגישה לפחות בשורה אחת — בלעדיו התעריף יוצא 0'
                    : `מילוי ${withLength.length} תעריפים לפי ₪${worked} לשעה × אורך הפגישה`}
                onClick={() => setTar({ ...tar, ...Object.fromEntries(withLength.map((m) =>
                  [m.field, String(perMeeting(Number(worked) || 0, Number(len[m.len]) || 0))])) })}
                className="text-[11px] text-blue-600 hover:underline disabled:text-gray-300 disabled:no-underline">
                ↧ מלא לפי התעריף השעתי{hasAnyLength && withLength.length < TARIFFS.length
                  ? ` (${withLength.length} מתוך ${TARIFFS.length})` : ''}
              </button>
            </div>
            <div className="grid grid-cols-2 gap-3">
              {TARIFFS.map((m) => (
                <div key={m.field} className="text-xs text-gray-500">
                  {m.label}
                  <div className="flex items-center gap-1.5 mt-1">
                    <span className="text-gray-500">₪</span>
                    <input type="number" step="0.01" value={tar[m.field]}
                      aria-label={`${m.label} — תעריף`}
                      onChange={(e) => setTar({ ...tar, [m.field]: e.target.value })}
                      className="w-20 text-sm border border-gray-300 rounded px-2 py-1 tabular-nums" />
                    <input type="number" step="5" min="0" value={len[m.len]}
                      aria-label={`${m.label} — אורך בדקות`}
                      onChange={(e) => { setLen({ ...len, [m.len]: e.target.value }); setLenTouched(true); }}
                      className="w-16 text-sm border border-gray-300 rounded px-2 py-1 tabular-nums" />
                    <span className="text-gray-500">דק׳</span>
                  </div>
                  {/* The reverse direction, per row: what this tariff works out to per
                      hour. Shown rather than applied, because four rows can imply four
                      different hourly rates and only a human knows which one is "the"
                      rate. Clicking adopts that row's. */}
                  {(() => {
                    const ph = perHour(Number(tar[m.field]) || 0, Number(len[m.len]) || 0);
                    if (!ph) return null;
                    const same = ph === (Number(worked) || 0);
                    return (
                      <button type="button" onClick={() => setWorked(String(ph))}
                        title={same ? 'זהה לתעריף השעתי הנוכחי' : `קביעת התעריף השעתי ל-₪${ph}`}
                        className={`mt-1 text-[11px] ${same ? 'text-gray-500 cursor-default' : 'text-blue-600 hover:underline'}`}>
                        ≡ ₪{ph} לשעה{same ? ' ✓' : ''}
                      </button>
                    );
                  })()}
                </div>
              ))}
            </div>
            <div className="mt-2 text-[11px] text-gray-500">
              התשלום נקבע לפי התעריף בלבד. האורך משמש להשוואה מול היומן — פגישה שאורכה שונה מהמוגדר תסומן כסלוט חריג.
            </div>
          </div>

          <div>
            <div className="text-xs font-medium text-gray-500 mb-2">no-show</div>
            <div className="flex items-center gap-3">
              <label className="text-xs text-gray-500">
                % מהתעריף
                <div className="flex items-center gap-1 mt-1">
                  <input type="number" step="1" min="0" max="100" value={noshowPct}
                    onChange={(e) => setNoshowPct(e.target.value)}
                    className="w-20 text-sm border border-gray-300 rounded px-2 py-1 tabular-nums" />
                  <span className="text-gray-500">%</span>
                </div>
              </label>
              <div className="text-xs text-gray-500 self-end pb-1.5">
                {TARIFFS.map((m) => {
                  const full = Number(tar[m.field]) || 0;
                  if (!full) return null;
                  return (
                    <span key={m.field} className="ml-3 whitespace-nowrap">
                      {m.label} ₪{full} → <span className="font-medium text-gray-700">
                        ₪{(full * (Number(noshowPct) || 0) / 100).toFixed(2)}
                      </span>
                    </span>
                  );
                })}
              </div>
            </div>
          </div>

          <div className="flex flex-wrap gap-4">
            <label className="text-xs text-gray-500">
              תעריף שעתי (₪)
              <input type="number" step="0.01" value={worked} onChange={(e) => setWorked(e.target.value)}
                className="block mt-1 w-24 text-sm border border-gray-300 rounded px-2 py-1 tabular-nums" />
            </label>
            <label className="text-xs text-gray-500">
              שעה לא-קלינית (₪)
              <input type="number" step="0.01" value={nonClinical} onChange={(e) => setNonClinical(e.target.value)}
                className="block mt-1 w-24 text-sm border border-gray-300 rounded px-2 py-1 tabular-nums" />
            </label>
            {/* Days come from /exec/oncall — payroll multiplies, it never asks anyone
                to retype a day count (Ohad 2026-09-01). Shown only to someone ON that
                schedule: everyone else saw the default 1500 and read it as money
                (Basel 01/11; Ohad 02/11: "רק למי שמופיע בסידור כוננות וזהו"). */}
            {t.oncall_doctor && (
              <label className="text-xs text-gray-500">
                כוננות (₪ ליום)
                <input type="number" step="0.01" value={oncall} onChange={(e) => setOncall(e.target.value)}
                  title="מספר ימי הכוננות מגיע מלוח הכוננות; כאן נקבע רק התעריף ליום"
                  className="block mt-1 w-24 text-sm border border-gray-300 rounded px-2 py-1 tabular-nums" />
              </label>
            )}
            <label className="text-xs text-gray-500">
              ניכוי מס במקור (%)
              <input type="number" step="1" value={wh} onChange={(e) => setWh(e.target.value)}
                className="block mt-1 w-20 text-sm border border-gray-300 rounded px-2 py-1 tabular-nums" />
            </label>
          </div>

          {isPsy && (
            <div className="pt-4 border-t">
              <div className="flex items-baseline justify-between mb-2">
                <div className="text-xs font-medium text-gray-500">בונוס</div>
                <div className="text-[11px] text-gray-500">
                  {personalBonus || bonusTouched ? 'הגדרה אישית לרופא זה' : 'לפי מדיניות כלל-הרוסטר'}
                </div>
              </div>
              <BonusFields
                draft={bonus} enabled={bonusEnabled}
                onChange={(d) => { setBonus(d); setBonusTouched(true); }}
                onEnabledChange={(v) => { setBonusEnabled(v); setBonusTouched(true); }}
              />
              {bonusTouched && !personalBonus && (
                <div className="mt-2 text-[11px] text-amber-700 bg-amber-50 rounded px-2 py-1.5">
                  שינוי כאן מנתק את הרופא ממדיניות ברירת המחדל — מכאן והלאה הוא לפי ההגדרה הזו בלבד.
                </div>
              )}
            </div>
          )}

          {error && (
            <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
              {error}
            </div>
          )}

          {/* The card reads ₪0 and the meetings price at ₪0, and nothing on screen says
              so. This is the line that would have caught Rami Khouri (47 meetings) and
              Vered Levy before payroll ran (Ohad, 2026-10-06). */}
          {noComp && (
            <div className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
              אין תעריף ואין תעריף שעתי — כל הפגישות של המטפל/ת הזה יחושבו כ-₪0.00
            </div>
          )}

          <div className="flex items-center gap-2 pt-2 border-t">
            <button onClick={save} disabled={saving}
              className="text-sm bg-blue-600 disabled:bg-gray-300 text-white rounded-lg px-6 py-2 font-medium">
              {saving ? 'שומר…' : 'שמור'}
            </button>
            <button onClick={onClose} className="text-sm text-gray-500 rounded px-4 py-2">ביטול</button>
            {!isNew && (
              <div className="mr-auto flex items-center gap-1">
                <button onClick={archive}
                  className="text-sm text-gray-500 hover:text-gray-700 rounded px-3 py-2">
                  {t.archived ? '↩︎ שחזור מהארכיון' : '🗄 העברה לארכיון'}
                </button>
                <button onClick={remove} title={`מחיקה מחודש ${month} והלאה`}
                  className="text-sm text-gray-500 hover:text-red-600 rounded px-3 py-2">
                  🗑 מחיקה
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
